import {
  BaseQueryFn,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import {addBreadcrumb} from '@sentry/browser';

// eslint-disable-next-line no-restricted-imports
import {isNotNil, not, omit, path} from 'ramda';

import {browserStorageKey, shouldDisableMandatoryFields} from '@dms/config';
import {getWorkspaceFromUri} from '@dms/routes';

import {
  BaseQueryExtraOptions,
  CI_MODE_ON,
  parseZodSchema,
  ParseZodSchemaQueryInfo,
} from 'shared/web';

import {captureBaseQueryError} from './captureBaseQueryError';
import {isCI} from './constants/isCI';
import {isDev} from './constants/isDev';
import {synchronizeTeasServerClock} from './getTeasServerNow';
import {initializeAxios} from './initializeAxios';
import {refreshAccessToken} from './refreshAccessToken';
import {resolveWorkspaceTarget} from './resolveWorkspaceTarget';
import {FetchBaseQueryArgs} from './types/FetchBaseQueryArgs';
import {getWorktreeHeaders} from './utils/getWorktreeHeaders';

const RETRY_DELAY = 3_000;
const INFRASTRUCTURE_ERRORS_CODES = [502, 503, 504];
const OAUTH_TOKEN_PATH = '/dms/v1/auth/oauth/token';

const getArgsWithAuthorization = (
  args: FetchBaseQueryArgs,
  branch?: string,
  workspaceHeader?: string
) => {
  const accessToken = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);
  const isCiModeOn = localStorage.getItem(browserStorageKey.CI_MODE) === CI_MODE_ON;

  const headers = {
    'accept-language': localStorage.getItem(browserStorageKey.LAST_KNOWN_LANGUAGE),
    'x-country-format': 'iso', // https://carvago.slack.com/archives/G01AN5XCXGX/p1634202539053900
    credentials: 'include',
    ...(accessToken ? {Authorization: `Bearer ${accessToken}`} : {}),
    ...(branch ? {'X-Branch': branch} : {}),
    ...(workspaceHeader ? {'X-Workspace': workspaceHeader} : {}),
    ...(isCiModeOn ? {'x-debug-mode': 'true'} : {}),
    ...getWorktreeHeaders(),
    ...args.headers,
  };

  return {...omit(['omitHeaders'], args), headers: omit(args.omitHeaders ?? [], headers)};
};

export const teasBaseQuery: BaseQueryFn<
  FetchBaseQueryArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions,
  FetchBaseQueryMeta
> = async (rawArgs, api, extraOptions) => {
  initializeAxios(api);
  const activeBranchId = path<string>(['branch', 'activeBranchId'], api.getState());

  // Resolved per request: an endpoint may pass a `workspace` arg (stripped before the request
  // goes out); without one, the workspace derived from the current location applies.
  const {workspace: argsWorkspace, ...restArgs} = rawArgs;
  const {baseUrl, workspaceHeader} = resolveWorkspaceTarget(
    process.env.API_URL,
    argsWorkspace ?? getWorkspaceFromUri().workspace
  );
  const fetchBaseQueryInstance = fetchBaseQuery({baseUrl});

  let args: FetchBaseQueryArgs = restArgs;

  // Override validateMandatoryFields if dev setting is enabled
  if (shouldDisableMandatoryFields() && isNotNil(args.params?.validateMandatoryFields)) {
    args = {
      ...args,
      params: {
        ...args.params,
        validateMandatoryFields: false,
      },
    };
  }

  const queryInfo: Omit<ParseZodSchemaQueryInfo, 'schemaType'> = {
    method: args.method,
    endpointName: api.endpoint,
    url: args.url,
  };

  if (isDev && not(isCI) && extraOptions && extraOptions.requestSchema) {
    parseZodSchema({
      data: args.params,
      schema: extraOptions.requestSchema,
      queryInfo: {...queryInfo, schemaType: 'request'},
    });
  }

  let result = await fetchBaseQueryInstance(
    getArgsWithAuthorization(args, activeBranchId, workspaceHeader),
    api,
    extraOptions
  );

  if (isDev && not(isCI) && extraOptions && extraOptions.responseSchema && 'data' in result) {
    parseZodSchema({
      data: result.data,
      schema: extraOptions.responseSchema,
      queryInfo: {...queryInfo, schemaType: 'response'},
    });
  }

  /**
   * This retry logic is used to handle infrastructure errors. This type of errors are often ad-hoc
   * and not related to the request payload, so retrying the request should help.
   * We also try to retry general 500 errors as this may also help.
   * https://carvago.atlassian.net/browse/T20-80058
   */
  if (result.error && !extraOptions?.skipInfrastructureRetry) {
    if (
      INFRASTRUCTURE_ERRORS_CODES.includes(
        Number(result.meta?.response?.status ?? result.error.status)
      )
    ) {
      addBreadcrumb({
        category: 'api',
        message: `Retrying PHP API request ${args.url}, because response status is ${result.meta?.response?.status ?? result.error.status}`,
        level: 'error',
      });
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      result = await fetchBaseQueryInstance(
        getArgsWithAuthorization(args, activeBranchId, workspaceHeader),
        api,
        extraOptions
      );
    }
  }

  if (result.error && typeof result.error.status === 'number' && result.error.status > 500) {
    captureBaseQueryError(result.error, args, api);
  }

  // A 401 from the oauth code exchange means the grant was rejected — a token refresh or a
  // redirect to login cannot fix it and would loop; the IamRedirect page handles the error.
  if (result.error && result.error.status === 401 && args.url !== OAUTH_TOKEN_PATH) {
    const refreshed = await refreshAccessToken(api.dispatch, api.getState);

    if (refreshed) {
      result = await fetchBaseQueryInstance(
        getArgsWithAuthorization(args, activeBranchId, workspaceHeader),
        api,
        extraOptions
      );
    }
  }

  synchronizeTeasServerClock(result.meta?.response);

  return result;
};
